import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  categoriesQuery,
  settingsQuery,
  banksQuery,
  subcategoriesQuery,
  companiesQuery,
  districtsQuery,
  citiesQuery,
  areasQuery,
} from "@/project/lib/queries";
import MotishaService from "@/services/APIs/MotishaService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";



export const Route = createFileRoute("/masters")({
  head: () => ({
    meta: [
      { title: "Masters & Settings · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Manage assistance categories and the letterhead details printed on every recommendation letter.",
      },
      { property: "og:title", content: "Masters & Settings" },
      {
        property: "og:description",
        content: "Manage categories and letterhead settings.",
      },
    ],
  }),
  component: MastersPage,
});

const SETTING_FIELDS: { key: string; label: string; long?: boolean }[] = [
  { key: "org_heading", label: "Organisation Heading" },
  { key: "managed_by", label: "Managed By" },
  { key: "trust_regd_no", label: "Trust Registration No." },
  { key: "csr_no", label: "CSR No." },
  { key: "office_address", label: "Office Address", long: true },
  { key: "mobile", label: "Office Mobile" },
  { key: "email", label: "Office Email" },
  { key: "signatory_name", label: "Signatory Name" },
  { key: "signatory_mobile", label: "Signatory Mobile" },
  { key: "documents_note", label: "Documents Column Heading" },
  { key: "closing_text", label: "Closing Paragraph", long: true },
  {
    key: "print_footer",
    label: "Footer Validity Text (use {DAYS})",
    long: true,
  },
  { key: "letter_validity_days", label: "Letter Validity (days)" },
  { key: "default_bank_name", label: "Default Bank Name" },
];

function MastersPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState("letterhead");

  const { data: settings } = useQuery({
    ...settingsQuery,
    enabled: activeTab === "letterhead",
  });

  const [form, setForm] = useState<Record<string, string>>({});
  useEffect(() => {
    if (settings) {
      setForm(settings);
    }
  }, [settings]);

  const { data: categories } = useQuery({
    ...categoriesQuery,
    enabled: activeTab === "categories",
  });
  const { data: banks } = useQuery({
    ...banksQuery,
    enabled: activeTab === "banks",
  });
  const { data: subcategories } = useQuery({
    ...subcategoriesQuery,
    enabled: activeTab === "categories",
  });
  const { data: organizations } = useQuery({
    ...companiesQuery,
    enabled: activeTab === "orgs",
  });
  const { data: districts } = useQuery({
    ...districtsQuery,
    enabled:
      activeTab === "districts" || activeTab === "cities" || activeTab === "orgs" || activeTab === "banks",
  });
  const { data: cities } = useQuery({
    ...citiesQuery,
    enabled:
      activeTab === "cities" || activeTab === "areas" || activeTab === "orgs" || activeTab === "banks",
  });
  const { data: areas } = useQuery({
    ...areasQuery,
    enabled: activeTab === "areas" || activeTab === "orgs",
  });

  const [newCat, setNewCat] = useState("");
  const [newSub, setNewSub] = useState("");
  const [subCatId, setSubCatId] = useState("");

  const [newDistrict, setNewDistrict] = useState("");
  const [newCity, setNewCity] = useState("");
  const [cityDistrictId, setCityDistrictId] = useState("");
  const [newArea, setNewArea] = useState("");
  const [areaCityId, setAreaCityId] = useState("");

  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [companyDraft, setCompanyDraft] = useState<any>(null);

  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<any>(null);
  const [editingDistrict, setEditingDistrict] = useState<any>(null);
  const [editingCity, setEditingCity] = useState<any>(null);
  const [editingArea, setEditingArea] = useState<any>(null);
  const [editingBank, setEditingBank] = useState<any>(null);
  const [isAddBankOpen, setIsAddBankOpen] = useState(false);
  const [isAddDistrictOpen, setIsAddDistrictOpen] = useState(false);
  const [isAddCityOpen, setIsAddCityOpen] = useState(false);
  const [isAddAreaOpen, setIsAddAreaOpen] = useState(false);



  const [bank, setBank] = useState({
    name: "",
    branch: "",
    ifsc: "",
    district_id: "",
    city_id: "",
  });

  const saveSettings = async () => {
    const rows = Object.entries(form).map(([key, value]) => ({ key, value }));
    const { error } = await supabase
      .from("app_settings")
      .upsert(rows, { onConflict: "key" });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Letterhead & settings saved");
    qc.invalidateQueries({ queryKey: ["settings"] });
  };

  const addCategory = async () => {
    if (!newCat.trim()) {
      return;
    }
    try {
      await MotishaService.createCategory({
        name: newCat.trim(),
        is_active: true,
      });
      setNewCat("");
      toast.success("Category added");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to add category");
    }
  };

  const addSubcategory = async () => {
    if (!subCatId || !newSub.trim()) {
      toast.error("Select a category and enter a subcategory name");
      return;
    }
    try {
      await MotishaService.createSubcategory({
        category_id: subCatId,
        name: newSub.trim(),
        is_active: true,
      });
      setNewSub("");
      toast.success("Subcategory added");
      qc.invalidateQueries({ queryKey: ["assistance_subcategories"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to add subcategory");
    }
  };

  const removeSubcategory = async (id: string) => {
    try {
      await MotishaService.deleteSubcategory(id);
      toast.success("Subcategory removed");
      qc.invalidateQueries({ queryKey: ["assistance_subcategories"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to remove subcategory");
    }
  };

  const addBank = async () => {
    if (!bank.name.trim()) {
      toast.error("Bank name is required");
      return;
    }
    if (!bank.district_id) {
      toast.error("District is required");
      return;
    }
    if (!bank.city_id) {
      toast.error("City is required");
      return;
    }
    try {
      await MotishaService.createBank({
        bank_name: bank.name.trim(),
        branch: bank.branch.trim(),
        ifsc: bank.ifsc.trim().toUpperCase(),
        district_id: bank.district_id,
        city_id: bank.city_id,
        is_active: true,
      });
      setBank({ name: "", branch: "", ifsc: "", district_id: "", city_id: "" });
      setIsAddBankOpen(false);
      toast.success("Bank added");
      qc.invalidateQueries({ queryKey: ["bank_master"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to add bank");
    }
  };

  const toggleBank = async (id: string, active: boolean) => {
    try {
      const b = banks?.find((item: any) => item.id === id);
      if (b) {
        await MotishaService.updateBank(id, {
          bank_name: b.bank_name || b.name,
          branch: b.branch || "",
          ifsc: b.ifsc || "",
          district_id: b.district_id,
          city_id: b.city_id,
          is_active: active,
        });
        qc.invalidateQueries({ queryKey: ["bank_master"] });
        toast.success(`Bank ${active ? "activated" : "deactivated"}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const removeBank = async (id: string) => {
    try {
      await MotishaService.deleteBank(id);
      toast.success("Bank deleted");
      qc.invalidateQueries({ queryKey: ["bank_master"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to delete bank");
    }
  };

  const toggleCategory = async (id: string, active: boolean) => {
    try {
      const cat = categories?.find((c: any) => c.id === id);
      if (cat) {
        await MotishaService.updateCategory(id, {
          name: cat.name,
          is_active: active,
        });
        toast.success(`Category ${active ? "activated" : "deactivated"}`);
        qc.invalidateQueries({ queryKey: ["categories"] });
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to update category");
    }
  };

  // District CRUD Handlers
  const addDistrict = async () => {
    if (!newDistrict.trim()) return;
    try {
      await MotishaService.createDistrict({
        name: newDistrict.trim(),
        is_active: true,
      });
      setNewDistrict("");
      setIsAddDistrictOpen(false);
      toast.success("District added");
      qc.invalidateQueries({ queryKey: ["districts"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to add district");
    }
  };

  const toggleDistrict = async (id: string, active: boolean, name: string) => {
    try {
      await MotishaService.updateDistrict(id, { name, is_active: active });
      qc.invalidateQueries({ queryKey: ["districts"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to update district");
    }
  };

  const removeDistrict = async (id: string) => {
    try {
      await MotishaService.deleteDistrict(id);
      toast.success("District deleted");
      qc.invalidateQueries({ queryKey: ["districts"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to delete district");
    }
  };

  // City CRUD Handlers
  const addCity = async () => {
    if (!cityDistrictId || !newCity.trim()) {
      toast.error("Select a district and enter a city name");
      return;
    }
    try {
      await MotishaService.createCity({
        district_id: cityDistrictId,
        name: newCity.trim(),
        is_active: true,
      });
      setNewCity("");
      setCityDistrictId("");
      setIsAddCityOpen(false);
      toast.success("City added");
      qc.invalidateQueries({ queryKey: ["cities"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to add city");
    }
  };

  const toggleCity = async (id: string, active: boolean, name: string, districtId: string) => {
    try {
      await MotishaService.updateCity(id, { name, district_id: districtId, is_active: active });
      qc.invalidateQueries({ queryKey: ["cities"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to update city");
    }
  };

  const removeCity = async (id: string) => {
    try {
      await MotishaService.deleteCity(id);
      toast.success("City deleted");
      qc.invalidateQueries({ queryKey: ["cities"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to delete city");
    }
  };

  // Area CRUD Handlers
  const addArea = async () => {
    if (!areaCityId || !newArea.trim()) {
      toast.error("Select a city and enter an area name");
      return;
    }
    try {
      await MotishaService.createArea({
        city_id: areaCityId,
        name: newArea.trim(),
        is_active: true,
      });
      setNewArea("");
      setAreaCityId("");
      setIsAddAreaOpen(false);
      toast.success("Area added");
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to add area");
    }
  };

  const toggleArea = async (id: string, active: boolean, name: string, cityId: string) => {
    try {
      await MotishaService.updateArea(id, { name, city_id: cityId, is_active: active });
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to update area");
    }
  };

  const removeArea = async (id: string) => {
    try {
      await MotishaService.deleteArea(id);
      toast.success("Area deleted");
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to delete area");
    }
  };

  // Company CRUD Handlers
  const saveCompany = async () => {
    if (!companyDraft) return;
    if (!companyDraft.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!companyDraft.district_id) {
      toast.error("District is required");
      return;
    }
    if (!companyDraft.city_id) {
      toast.error("City is required");
      return;
    }
    if (!companyDraft.area_id) {
      toast.error("Area is required");
      return;
    }

    const { id, active, ...rest } = companyDraft;
    const payload = {
      ...rest,
      name: rest.name.trim(),
      is_active: active,
    };

    try {
      if (id) {
        await MotishaService.updateCompany(id, payload);
        toast.success("Company updated");
      } else {
        await MotishaService.createCompany(payload);
        toast.success("Company created");
      }
      setIsCompanyOpen(false);
      setCompanyDraft(null);
      qc.invalidateQueries({ queryKey: ["companies"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to save company");
    }
  };

  const toggleCompany = async (id: string, active: boolean) => {
    try {
      const org = organizations?.find((o: any) => o.id === id);
      if (org) {
        await MotishaService.updateCompany(id, {
          name: org.name,
          district_id: org.district_id,
          city_id: org.city_id,
          area_id: org.area_id,
          contact: org.contact_number || "",
          email: org.email || "",
          visiting: org.visiting_day || "",
          is_active: active,
        });
        qc.invalidateQueries({ queryKey: ["companies"] });
        toast.success(`Company ${active ? "activated" : "deactivated"}`);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const removeCompany = async (id: string) => {
    try {
      await MotishaService.deleteCompany(id);
      toast.success("Company deleted");
      qc.invalidateQueries({ queryKey: ["companies"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to delete company");
    }
  };

  // Edit Handlers for other Masters
  const saveEditedCategory = async () => {
    if (!editingCategory || !editingCategory.name.trim()) return;
    try {
      await MotishaService.updateCategory(editingCategory.id, {
        name: editingCategory.name.trim(),
        is_active: editingCategory.is_active,
      });
      setEditingCategory(null);
      toast.success("Category updated");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update category");
    }
  };

  const saveEditedSubcategory = async () => {
    if (!editingSubcategory || !editingSubcategory.name.trim() || !editingSubcategory.category_id) return;
    try {
      await MotishaService.updateSubcategory(editingSubcategory.id, {
        name: editingSubcategory.name.trim(),
        category_id: editingSubcategory.category_id,
        is_active: editingSubcategory.is_active,
      });
      setEditingSubcategory(null);
      toast.success("Subcategory updated");
      qc.invalidateQueries({ queryKey: ["assistance_subcategories"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update subcategory");
    }
  };

  const saveEditedDistrict = async () => {
    if (!editingDistrict || !editingDistrict.name.trim()) return;
    try {
      await MotishaService.updateDistrict(editingDistrict.id, {
        name: editingDistrict.name.trim(),
        is_active: editingDistrict.is_active,
      });
      setEditingDistrict(null);
      toast.success("District updated");
      qc.invalidateQueries({ queryKey: ["districts"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update district");
    }
  };

  const saveEditedCity = async () => {
    if (!editingCity || !editingCity.name.trim() || !editingCity.district_id) return;
    try {
      await MotishaService.updateCity(editingCity.id, {
        name: editingCity.name.trim(),
        district_id: editingCity.district_id,
        is_active: editingCity.is_active,
      });
      setEditingCity(null);
      toast.success("City updated");
      qc.invalidateQueries({ queryKey: ["cities"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update city");
    }
  };

  const saveEditedArea = async () => {
    if (!editingArea || !editingArea.name.trim() || !editingArea.city_id) return;
    try {
      await MotishaService.updateArea(editingArea.id, {
        name: editingArea.name.trim(),
        city_id: editingArea.city_id,
        is_active: editingArea.is_active,
      });
      setEditingArea(null);
      toast.success("Area updated");
      qc.invalidateQueries({ queryKey: ["areas"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update area");
    }
  };

  const saveEditedBank = async () => {
    if (!editingBank || !editingBank.name.trim()) return;
    if (!editingBank.district_id) {
      toast.error("District is required");
      return;
    }
    if (!editingBank.city_id) {
      toast.error("City is required");
      return;
    }
    try {
      await MotishaService.updateBank(editingBank.id, {
        bank_name: editingBank.name.trim(),
        branch: editingBank.branch.trim(),
        ifsc: editingBank.ifsc.trim().toUpperCase(),
        district_id: editingBank.district_id,
        city_id: editingBank.city_id,
        is_active: editingBank.active,
      });
      setEditingBank(null);
      toast.success("Bank updated");
      qc.invalidateQueries({ queryKey: ["bank_master"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update bank");
    }
  };




  return (
    <Shell>
      <PageHeader
        title="Masters & Settings"
        subtitle="Categories and letterhead content"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="letterhead">Letterhead</TabsTrigger>
          <TabsTrigger value="categories">
            Categories &amp; Subcategories
          </TabsTrigger>
          <TabsTrigger value="banks">Bank Master</TabsTrigger>
          <TabsTrigger value="orgs">Company Master</TabsTrigger>
          <TabsTrigger value="districts">District Master</TabsTrigger>
          <TabsTrigger value="cities">City Master</TabsTrigger>
          <TabsTrigger value="areas">Area Master</TabsTrigger>
        </TabsList>

        <TabsContent value="letterhead" className="pt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Letter Content & Trust Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {SETTING_FIELDS.map((f: any) => (
                <div
                  key={f.key}
                  className={
                    f.long ? "space-y-1.5 md:col-span-2" : "space-y-1.5"
                  }
                >
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {f.label}
                  </Label>
                  {f.long ? (
                    <Textarea
                      value={form[f.key] ?? ""}
                      onChange={e =>
                        setForm({ ...form, [f.key]: e.target.value })
                      }
                    />
                  ) : (
                    <Input
                      value={form[f.key] ?? ""}
                      onChange={e =>
                        setForm({ ...form, [f.key]: e.target.value })
                      }
                    />
                  )}
                </div>
              ))}
              <div className="md:col-span-2">
                <Button onClick={saveSettings}>
                  <Save className="mr-2 h-4 w-4" /> Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="pt-4">
          <Card>
            <CardContent className="p-4">
              <div className="mb-4 flex gap-3">
                <Input
                  placeholder="New category name (e.g. Housing)"
                  value={newCat}
                  onChange={e => setNewCat(e.target.value)}
                />
                <Button onClick={addCategory}>
                  <Plus className="mr-2 h-4 w-4" /> Add
                </Button>
              </div>
              <div className="mb-4 flex flex-wrap gap-3">
                <Select value={subCatId} onValueChange={setSubCatId}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Category for subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories ?? []).map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="max-w-xs"
                  placeholder="New subcategory (e.g. Surgery)"
                  value={newSub}
                  onChange={e => setNewSub(e.target.value)}
                />
                <Button variant="outline" onClick={addSubcategory}>
                  <Plus className="mr-2 h-4 w-4" /> Add Subcategory
                </Button>
              </div>

              <div className="divide-y divide-border">
                {(categories ?? []).map((c: any) => (
                  <div key={c.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{c.name}</p>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            onClick={() =>
                              setEditingCategory({
                                id: c.id,
                                name: c.name,
                                is_active: c.active,
                              })
                            }
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {c.description && (
                          <p className="text-sm text-muted-foreground">
                            {c.description}
                          </p>
                        )}
                      </div>
                      <Switch
                        checked={c.active}
                        onCheckedChange={v => toggleCategory(c.id, v)}
                        aria-label="Active"
                      />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(subcategories ?? [])
                        .filter((s: any) => s.category_id === c.id)
                        .map((s: any) => (
                          <Badge
                            key={s.id}
                            variant="secondary"
                            className="gap-1.5 py-1"
                          >
                            <span>{s.name}</span>
                            <button
                              type="button"
                              aria-label={`Edit ${s.name}`}
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                setEditingSubcategory({
                                  id: s.id,
                                  name: s.name,
                                  category_id: s.category_id,
                                  is_active: s.active,
                                })
                              }
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Remove ${s.name}`}
                              className="text-destructive hover:text-red-700"
                              onClick={() => removeSubcategory(s.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      {!(subcategories ?? []).some(
                        (s: any) => s.category_id === c.id
                      ) && (
                          <span className="text-xs text-muted-foreground">
                            No subcategories yet.
                          </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="banks" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Bank Master</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setBank({
                    name: "",
                    branch: "",
                    ifsc: "",
                    district_id: "",
                    city_id: "",
                  });
                  setIsAddBankOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add Bank
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2">Bank</th>
                      <th className="px-3 py-2">Branch</th>
                      <th className="px-3 py-2">IFSC</th>
                      <th className="px-3 py-2">City / District</th>
                      <th className="px-3 py-2">Active</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(banks ?? []).map((b: any) => (
                      <tr key={b.id}>
                        <td className="px-3 py-2 font-medium">{b.name}</td>
                        <td className="px-3 py-2">{b.branch ?? "—"}</td>
                        <td className="px-3 py-2">{b.ifsc ?? "—"}</td>
                        <td className="px-3 py-2">
                          {[
                            cities?.find((c: any) => c.id === b.city_id)?.name,
                            districts?.find((d: any) => d.id === b.district_id)?.name,
                          ].filter(Boolean).join(" / ") || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <Switch
                            checked={b.active}
                            onCheckedChange={v => toggleBank(b.id, v)}
                            aria-label="Active"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                setEditingBank({
                                  id: b.id,
                                  name: b.name,
                                  branch: b.branch ?? "",
                                  ifsc: b.ifsc ?? "",
                                  district_id: b.district_id ?? "",
                                  city_id: b.city_id ?? "",
                                  active: b.active,
                                })
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              aria-label={`Delete ${b.name}`}
                              onClick={() => removeBank(b.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!(banks ?? []).length && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No banks added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* District Master */}
        <TabsContent value="districts" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">District Master</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setNewDistrict("");
                  setIsAddDistrictOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add District
              </Button>
            </CardHeader>
            <CardContent className="p-4">

              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left">District Name</th>
                      <th className="px-3 py-2 text-left w-24">Active</th>
                      <th className="px-3 py-2 text-right w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(districts ?? []).map((d: any) => (
                      <tr key={d.id}>
                        <td className="px-3 py-2 font-medium">{d.name}</td>
                        <td className="px-3 py-2">
                          <Switch
                            checked={d.active}
                            onCheckedChange={v => toggleDistrict(d.id, v, d.name)}
                            aria-label="Active"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                setEditingDistrict({
                                  id: d.id,
                                  name: d.name,
                                  is_active: d.active,
                                })
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              aria-label={`Delete ${d.name}`}
                              onClick={() => removeDistrict(d.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!(districts ?? []).length && (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No districts added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* City Master */}
        <TabsContent value="cities" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">City Master</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setNewCity("");
                  setCityDistrictId("");
                  setIsAddCityOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add City
              </Button>
            </CardHeader>
            <CardContent className="p-4">

              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left">City Name</th>
                      <th className="px-3 py-2 text-left">District</th>
                      <th className="px-3 py-2 text-left w-24">Active</th>
                      <th className="px-3 py-2 text-right w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cities ?? []).map((c: any) => {
                      const district = (districts ?? []).find((d: any) => d.id === c.district_id);
                      return (
                        <tr key={c.id}>
                          <td className="px-3 py-2 font-medium">{c.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{district?.name ?? "—"}</td>
                          <td className="px-3 py-2">
                            <Switch
                              checked={c.active}
                              onCheckedChange={v => toggleCity(c.id, v, c.name, c.district_id)}
                              aria-label="Active"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() =>
                                  setEditingCity({
                                    id: c.id,
                                    name: c.name,
                                    district_id: c.district_id,
                                    is_active: c.active,
                                  })
                                }
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                aria-label={`Delete ${c.name}`}
                                onClick={() => removeCity(c.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!(cities ?? []).length && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No cities added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Area Master */}
        <TabsContent value="areas" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Area Master</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setNewArea("");
                  setAreaCityId("");
                  setIsAddAreaOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add Area
              </Button>
            </CardHeader>
            <CardContent className="p-4">

              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left">Area Name</th>
                      <th className="px-3 py-2 text-left">City</th>
                      <th className="px-3 py-2 text-left w-24">Active</th>
                      <th className="px-3 py-2 text-right w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(areas ?? []).map((a: any) => {
                      const city = (cities ?? []).find((c: any) => c.id === a.city_id);
                      return (
                        <tr key={a.id}>
                          <td className="px-3 py-2 font-medium">{a.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{city?.name ?? "—"}</td>
                          <td className="px-3 py-2">
                            <Switch
                              checked={a.active}
                              onCheckedChange={v => toggleArea(a.id, v, a.name, a.city_id)}
                              aria-label="Active"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() =>
                                  setEditingArea({
                                    id: a.id,
                                    name: a.name,
                                    city_id: a.city_id,
                                    is_active: a.active,
                                  })
                                }
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                aria-label={`Delete ${a.name}`}
                                onClick={() => removeArea(a.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!(areas ?? []).length && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No areas added yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orgs" className="pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Company Master</CardTitle>
              <Button
                variant="outline"
                onClick={() => {
                  setCompanyDraft({
                    name: "",
                    district_id: "",
                    city_id: "",
                    area_id: "",
                    contact: "",
                    email: "",
                    visiting: "",
                    active: true,
                  });
                  setIsCompanyOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add Company
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="data-table w-full text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Area / City / District</th>
                    <th className="px-3 py-2">Contact</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Visiting</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(organizations ?? []).map((o: any) => (
                    <tr key={o.id}>
                      <td className="px-3 py-2 font-medium">{o.name}</td>
                      <td className="px-3 py-2">
                        {[
                          areas?.find((a: any) => a.id === o.area_id)?.name,
                          cities?.find((c: any) => c.id === o.city_id)?.name,
                          districts?.find((d: any) => d.id === o.district_id)?.name,
                        ].filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2">{o.contact_number ?? "—"}</td>
                      <td className="px-3 py-2">{o.email ?? "—"}</td>
                      <td className="px-3 py-2">{o.visiting_day ?? "—"}</td>
                      <td className="px-3 py-2">
                        <Switch
                          checked={o.active}
                          onCheckedChange={v => toggleCompany(o.id, v)}
                          aria-label="Active"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCompanyDraft({
                                id: o.id,
                                name: o.name,
                                district_id: o.district_id ?? "",
                                city_id: o.city_id ?? "",
                                area_id: o.area_id ?? "",
                                contact: o.contact_number ?? "",
                                email: o.email ?? "",
                                visiting: o.visiting_day ?? "",
                                active: o.active,
                              });
                              setIsCompanyOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive h-8 w-8"
                            aria-label={`Delete ${o.name}`}
                            onClick={() => removeCompany(o.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!(organizations ?? []).length && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-6 text-center text-muted-foreground"
                      >
                        No organisations added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isCompanyOpen} onOpenChange={setIsCompanyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {companyDraft?.id ? "Edit Company" : "New Company"}
            </DialogTitle>
          </DialogHeader>
          {companyDraft && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>Company Name *</Label>
                <Input
                  value={companyDraft.name}
                  onChange={e =>
                    setCompanyDraft({ ...companyDraft, name: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>District *</Label>
                <Select
                  value={companyDraft.district_id}
                  onValueChange={v =>
                    setCompanyDraft({
                      ...companyDraft,
                      district_id: v,
                      city_id: "",
                      area_id: "",
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select District" />
                  </SelectTrigger>
                  <SelectContent>
                    {(districts ?? []).map((d: any) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>City *</Label>
                <Select
                  value={companyDraft.city_id}
                  onValueChange={v =>
                    setCompanyDraft({
                      ...companyDraft,
                      city_id: v,
                      area_id: "",
                    })
                  }
                  disabled={!companyDraft.district_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {(cities ?? [])
                      .filter((c: any) => c.district_id === companyDraft.district_id)
                      .map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Area *</Label>
                <Select
                  value={companyDraft.area_id}
                  onValueChange={v =>
                    setCompanyDraft({ ...companyDraft, area_id: v })
                  }
                  disabled={!companyDraft.city_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Area" />
                  </SelectTrigger>
                  <SelectContent>
                    {(areas ?? [])
                      .filter((a: any) => a.city_id === companyDraft.city_id)
                      .map((a: any) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Contact Number</Label>
                <Input
                  value={companyDraft.contact}
                  onChange={e =>
                    setCompanyDraft({ ...companyDraft, contact: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={companyDraft.email}
                  onChange={e =>
                    setCompanyDraft({ ...companyDraft, email: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>Visiting Details</Label>
                <Input
                  placeholder="e.g. Monday - Friday · 9:00AM to 7:00PM"
                  value={companyDraft.visiting}
                  onChange={e =>
                    setCompanyDraft({ ...companyDraft, visiting: e.target.value })
                  }
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setIsCompanyOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveCompany}>
                  Save Company
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={v => !v && setEditingCategory(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>Category Name *</Label>
                <Input
                  value={editingCategory.name}
                  onChange={e =>
                    setEditingCategory({ ...editingCategory, name: e.target.value })
                  }
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingCategory(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedCategory}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Subcategory Dialog */}
      <Dialog open={!!editingSubcategory} onOpenChange={v => !v && setEditingSubcategory(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
          </DialogHeader>
          {editingSubcategory && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>Subcategory Name *</Label>
                <Input
                  value={editingSubcategory.name}
                  onChange={e =>
                    setEditingSubcategory({ ...editingSubcategory, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category *</Label>
                <Select
                  value={editingSubcategory.category_id}
                  onValueChange={v =>
                    setEditingSubcategory({ ...editingSubcategory, category_id: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories ?? []).map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingSubcategory(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedSubcategory}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit District Dialog */}
      <Dialog open={!!editingDistrict} onOpenChange={v => !v && setEditingDistrict(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit District</DialogTitle>
          </DialogHeader>
          {editingDistrict && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>District Name *</Label>
                <Input
                  value={editingDistrict.name}
                  onChange={e =>
                    setEditingDistrict({ ...editingDistrict, name: e.target.value })
                  }
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingDistrict(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedDistrict}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit City Dialog */}
      <Dialog open={!!editingCity} onOpenChange={v => !v && setEditingCity(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit City</DialogTitle>
          </DialogHeader>
          {editingCity && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>City Name *</Label>
                <Input
                  value={editingCity.name}
                  onChange={e =>
                    setEditingCity({ ...editingCity, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>District *</Label>
                <Select
                  value={editingCity.district_id}
                  onValueChange={v =>
                    setEditingCity({ ...editingCity, district_id: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select District" />
                  </SelectTrigger>
                  <SelectContent>
                    {(districts ?? []).map((d: any) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingCity(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedCity}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Area Dialog */}
      <Dialog open={!!editingArea} onOpenChange={v => !v && setEditingArea(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Area</DialogTitle>
          </DialogHeader>
          {editingArea && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>Area Name *</Label>
                <Input
                  value={editingArea.name}
                  onChange={e =>
                    setEditingArea({ ...editingArea, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>City *</Label>
                <Select
                  value={editingArea.city_id}
                  onValueChange={v =>
                    setEditingArea({ ...editingArea, city_id: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {(cities ?? []).map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingArea(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedArea}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Bank Dialog */}
      <Dialog open={!!editingBank} onOpenChange={v => !v && setEditingBank(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Bank Details</DialogTitle>
          </DialogHeader>
          {editingBank && (
            <div className="grid gap-4 py-4">
              <div className="space-y-1.5">
                <Label>Bank Name *</Label>
                <Input
                  value={editingBank.name}
                  onChange={e =>
                    setEditingBank({ ...editingBank, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Branch</Label>
                <Input
                  value={editingBank.branch}
                  onChange={e =>
                    setEditingBank({ ...editingBank, branch: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>IFSC</Label>
                <Input
                  value={editingBank.ifsc}
                  onChange={e =>
                    setEditingBank({ ...editingBank, ifsc: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>District *</Label>
                <Select
                  value={editingBank.district_id}
                  onValueChange={v =>
                    setEditingBank({ ...editingBank, district_id: v, city_id: "" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select District" />
                  </SelectTrigger>
                  <SelectContent>
                    {(districts ?? []).map((d: any) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>City *</Label>
                <Select
                  value={editingBank.city_id}
                  onValueChange={v =>
                    setEditingBank({ ...editingBank, city_id: v })
                  }
                  disabled={!editingBank.district_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {(cities ?? [])
                      .filter((c: any) => c.district_id === editingBank.district_id)
                      .map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingBank(null)}>
                  Cancel
                </Button>
                <Button onClick={saveEditedBank}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Bank Dialog */}
      <Dialog open={isAddBankOpen} onOpenChange={v => !v && setIsAddBankOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Bank Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label>Bank Name *</Label>
              <Input
                placeholder="Enter bank name"
                value={bank.name}
                onChange={e => setBank({ ...bank, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Branch</Label>
              <Input
                placeholder="Enter branch name"
                value={bank.branch}
                onChange={e => setBank({ ...bank, branch: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>IFSC</Label>
              <Input
                placeholder="Enter IFSC code"
                value={bank.ifsc}
                onChange={e => setBank({ ...bank, ifsc: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>District *</Label>
              <Select
                value={bank.district_id}
                onValueChange={v => setBank({ ...bank, district_id: v, city_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent>
                  {(districts ?? []).map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>City *</Label>
              <Select
                value={bank.city_id}
                onValueChange={v => setBank({ ...bank, city_id: v })}
                disabled={!bank.district_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select City" />
                </SelectTrigger>
                <SelectContent>
                  {(cities ?? [])
                    .filter((c: any) => c.district_id === bank.district_id)
                    .map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsAddBankOpen(false)}>
                Cancel
              </Button>
              <Button onClick={addBank}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add District Dialog */}
      <Dialog open={isAddDistrictOpen} onOpenChange={v => !v && setIsAddDistrictOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add District Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label>District Name *</Label>
              <Input
                placeholder="Enter district name (e.g. Ahmedabad District)"
                value={newDistrict}
                onChange={e => setNewDistrict(e.target.value)}
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsAddDistrictOpen(false)}>
                Cancel
              </Button>
              <Button onClick={addDistrict}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add City Dialog */}
      <Dialog open={isAddCityOpen} onOpenChange={v => !v && setIsAddCityOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add City Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label>District *</Label>
              <Select value={cityDistrictId} onValueChange={setCityDistrictId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent>
                  {(districts ?? []).map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>City Name *</Label>
              <Input
                placeholder="Enter city name (e.g. Ahmedabad)"
                value={newCity}
                onChange={e => setNewCity(e.target.value)}
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsAddCityOpen(false)}>
                Cancel
              </Button>
              <Button onClick={addCity}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Area Dialog */}
      <Dialog open={isAddAreaOpen} onOpenChange={v => !v && setIsAddAreaOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Area Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label>City *</Label>
              <Select value={areaCityId} onValueChange={setAreaCityId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select City" />
                </SelectTrigger>
                <SelectContent>
                  {(cities ?? []).map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Area Name *</Label>
              <Input
                placeholder="Enter area name (e.g. Satellite)"
                value={newArea}
                onChange={e => setNewArea(e.target.value)}
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setIsAddAreaOpen(false)}>
                Cancel
              </Button>
              <Button onClick={addArea}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
